import * as React from 'react'
import { styled } from '@mui/material/styles'
import { css } from '@emotion/react'

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'

import { I18n, useTranslation } from 'components/hooks/use-translation'
import useWindowSize from 'components/hooks/use-window-size'
import { useUser } from 'components/hooks/state'

import ResponsiveContainer from 'components/atomic/organisms/responsive-container'
import { IconName } from 'components/atomic/atoms/icon/components/custom-icons'
import MarkdownViewer from 'components/atomic/organisms/markdown-viewer'
import MainLayout from 'components/layouts/main-layout'
import Button from 'components/atomic/atoms/button'
import Text from 'components/atomic/atoms/text'
import Icon from 'components/atomic/atoms/icon'
import Chip from 'components/atomic/atoms/chip'
import { CTABuy, CTALead } from './cta-card'

import { computeUserCount, colorsFromItemType } from 'utils/paths'
import { Track, Path, PathGoal } from 'types/types'

const HeroWrapper = styled('div')`
    position: relative;
    border-bottom: ${({ theme }) => `1px solid ${theme.palette.divider}`};
`

const StyledContainer = styled(ResponsiveContainer)`
    padding-top: 4rem;
    padding-bottom: 3rem;
`

const BackgroundImage = styled('div')<{ type: PathGoal }>`
    background: url('/assets/lines2.png');
    background-repeat: no-repeat;
    background-position: right;
    background-size: cover;
    position: absolute;
    height: 100%;
    width: 100%;
    z-index: -1;
    opacity: 0.7;

    ${({ type }) => {
        if (type === 'career') {
            return css`
                filter: invert(61%) sepia(4%) saturate(7137%) hue-rotate(314deg) brightness(91%) contrast(100%);
            `
        }

        if (type === 'skill') {
            return css`
                filter: invert(67%) sepia(17%) saturate(5877%) hue-rotate(190deg) brightness(95%) contrast(91%);
            `
        }

        if (type === 'course') {
            return css`
                filter: invert(69%) sepia(52%) saturate(5228%) hue-rotate(242deg) brightness(93%) contrast(95%);
            `
        }
    }}

    ${({ theme }) => theme.breakpoints.down('md')} {
        opacity: 0.2;
    }
`

const Loading = () => {
    return (
        <MainLayout isFluid>
            <ResponsiveContainer maxWidth="lg">
                <Stack pt="3rem" spacing="1rem" direction="row">
                    <Skeleton variant="rounded" width="8rem" height="2rem" />
                    <Skeleton variant="circular" width="2rem" height="2rem" />
                    <Skeleton variant="circular" width="2rem" height="2rem" />
                </Stack>
                <Stack pt="1rem" spacing="1rem" direction="row">
                    <Stack pt="1rem" spacing="1rem" width="60%">
                        <Skeleton variant="rounded" width="40%" height="1rem" />
                        <Skeleton variant="rounded" width="95%" height="10rem" />
                        <Skeleton variant="rounded" width="95%" height="4rem" />
                    </Stack>
                    {/* <Box pt="1rem" spacing="1rem" width="30%"> */}
                    <Skeleton variant="rounded" width="40%" height="20rem" />

                    {/* </Box> */}
                </Stack>
                <Skeleton variant="rounded" width="8rem" height="2rem" />

                <Stack pt="4rem" spacing="1rem">
                    <Skeleton variant="rounded" width="100%" height="14rem" />
                    <Skeleton variant="rounded" width="100%" height="14rem" />
                </Stack>
            </ResponsiveContainer>
        </MainLayout>
    )
}

interface HeroProps {
    item: Track | Path
    itemType: PathGoal
}

const Hero = ({ item, itemType }: HeroProps) => {
    const { t }: I18n = useTranslation()
    const { height } = useWindowSize()
    const { user, loading: userLoading } = useUser()

    const features = React.useMemo(() => {
        const resources = Object.entries(item.resources_count)
            .filter(([key, value]) => value > 0 && key !== 'total')
            .map(([key, value]) => ({
                key,
                value,
                label:
                    value > 1
                        ? t(`resources:types.plurals.${key.toUpperCase()}`)
                        : t(`resources:types.${key.toUpperCase()}`),
            }))
            .sort((a, b) => b.value - a.value)

        const projects = resources.find((r) => r.key === 'project')

        const orderedResources = projects ? [projects, ...resources.filter((r) => r.key !== 'project')] : resources

        let resourceString = `${orderedResources[0].value} ${orderedResources[0].label}`

        if (orderedResources.length === 2) {
            resourceString = `${resourceString}, ${orderedResources[1].value} ${orderedResources[1].label}`
        } else if (orderedResources.length > 2) {
            resourceString = `${resourceString}, ${orderedResources[1].value} ${orderedResources[1].label} ${t(
                'path:hero.moreResources',
            )}`
        }

        const feat = [
            {
                icon: item.difficulty.toLowerCase() as IconName,
                value: <Text>{t(`common:difficultyExtended.${item.difficulty}`)}</Text>,
            },
            {
                icon: 'handyman',
                value: <Text>{`Contiene ${resourceString}`}</Text>,
            },
            {
                icon: 'language',
                value: <Text>{'In italiano e inglese'}</Text>,
            },
        ]

        if (item.completion_time) {
            feat.push({
                icon: 'timerOutlined',
                value: <Text>{item.completion_time}</Text>,
            })
        } else {
            feat.push({
                icon: 'timerOutlined',
                value: <Text>{`Durata totale ${(item.time_total / 60).toFixed(1)} ore`}</Text>,
            })
        }

        if (item.grants_certificate) {
            feat.push({
                icon: 'medal',
                value: <Text>{'Con certificato di completamento'}</Text>,
            })
        }

        return feat
    }, [item, t])

    const handleScrollTo = () => {
        window.scrollTo({ top: height * 0.7, left: 0, behavior: 'smooth' })
    }

    const CTACard = React.useCallback(() => {
        if (item.live && item.pro_preview && user?.subscription_status === 'ACTIVE') {
            return (
                <CTALead
                    _id={item._id}
                    item={item}
                    itemType={itemType}
                    title="Iniza ora"
                    description="Buona notizia, il corso è disponibile solo per gli utenti PRO come te."
                    buttonText="Inizia ora"
                    joinAction="enroll"
                    enrolledUsers={computeUserCount(item)}
                    proAlternative={false}
                />
            )
        } else if (item.live && item.pro_preview) {
            return (
                <CTALead
                    _id={item._id}
                    item={item}
                    itemType={itemType}
                    title="Assicurati un posto"
                    description="Il corso sarà disponibile a breve. Inserisci la tua email per essere il primo a sapere quando sarà disponibile."
                    buttonText="Entra in lista di attesa"
                    joinAction="pre-enroll"
                    enrolledUsers={computeUserCount(item)}
                    proAlternative={true}
                />
            )
        } else if (item.live && !item.pro_exclusive) {
            return (
                <CTALead
                    _id={item._id}
                    item={item}
                    itemType={itemType}
                    title="Iscriviti subito"
                    description="Segui il percorso, guadagna nuove skill e un certificato"
                    buttonText="Inizia ora, è gratis"
                    joinAction="enroll"
                    enrolledUsers={computeUserCount(item)}
                    proAlternative={false}
                />
            )
        } else if (item.live && item.pro_exclusive) {
            return (
                <CTABuy
                    _id={item._id}
                    itemType={itemType}
                    isPro={user?.subscription_status === 'ACTIVE'}
                    enrolledUsers={computeUserCount(item)}
                />
            )
        }

        return (
            <CTALead
                _id={item._id}
                item={item}
                itemType={itemType}
                title="Assicurati un posto"
                description="Entra in lista di attesa per essere notificato appena il corso sarà disponibile"
                buttonText="Entra in lista di attesa"
                joinAction="pre-enroll"
                enrolledUsers={computeUserCount(item)}
                proAlternative={false}
            />
        )
    }, [item, itemType, user?.subscription_status])

    const colors = React.useMemo(() => colorsFromItemType(itemType), [itemType])

    if (userLoading) return <Loading />

    return (
        <HeroWrapper>
            <BackgroundImage type={itemType} />
            <StyledContainer maxWidth="lg">
                <Grid container gap={4}>
                    {/* Left side */}
                    <Grid item xs={12} sm={12} md>
                        <Stack spacing={5}>
                            <Stack spacing={2}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={t(`common:pathGoal.${itemType}`)}
                                        sx={{ borderColor: colors[0], backgroundColor: colors[1] }}
                                    />

                                    {!item.live && <Chip variant="outlined" label={'Coming Soon'} size="small" />}

                                    {/* eslint-disable-next-line @next/next/no-img-element  */}
                                    <img
                                        alt="Italy flag"
                                        src={'/assets/flag-it.svg'}
                                        style={{ height: 20, width: 20, borderRadius: '50%' }}
                                    />

                                    {/* eslint-disable-next-line @next/next/no-img-element  */}
                                    <img
                                        alt="Italy flag"
                                        src={'/assets/flag-gb.svg'}
                                        style={{ height: 20, width: 20, borderRadius: '50%' }}
                                    />
                                </Stack>

                                {item?.categories.length && (
                                    <Text variant="subtitle2" fontWeight="bold">
                                        {item.categories
                                            .slice(0, 4)
                                            .map((cat) => cat.title)
                                            .join(' · ')}
                                    </Text>
                                )}
                            </Stack>

                            {/** Categories & Description */}
                            <Stack spacing={4}>
                                <Text variant="h2">{item.title}</Text>
                                <MarkdownViewer>{item.short_description}</MarkdownViewer>
                            </Stack>

                            <br />

                            {/** Bullet points */}
                            <Grid container alignItems="center" color="text.secondary">
                                {features.map((feat, i) => {
                                    return (
                                        <Grid item key={i} mr={2} mt={2}>
                                            <Chip
                                                size="medium"
                                                variant="outlined"
                                                label={feat.value}
                                                icon={
                                                    <Icon
                                                        name={feat.icon as IconName}
                                                        fontSize="small"
                                                        color="primary"
                                                    />
                                                }
                                            />
                                        </Grid>
                                    )
                                })}
                            </Grid>
                        </Stack>
                    </Grid>

                    {/* Right side */}
                    <Grid item xs={12} sm={12} md={4}>
                        {CTACard()}
                    </Grid>
                </Grid>

                {/*  Discover more */}
                <Box sx={{ width: '100%', marginTop: { xs: 4, md: 8 } }} textAlign="center">
                    <Button color="inherit" onClick={() => handleScrollTo()} endIcon={<KeyboardArrowDownIcon />}>
                        {t('path:buttons.discover')}
                    </Button>
                </Box>
            </StyledContainer>
        </HeroWrapper>
    )
}

export default Hero
